import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PRACTICE_INFO } from '../../data/navigation';
import s from './NewPatientForms.module.css';

const FORMS = [
  {
    id: 'registration',
    icon: '📋',
    title: 'New Patient Registration',
    badge: 'Required',
    badgeColor: '#0e8585',
    description:
      'Please download, print, and complete this form before your first dental appointment. Bringing it filled out saves time and helps us prepare for your visit.',
    fileName: 'Health-History-Form.pdf',
    filePath: '/forms/dental-health-history.pdf',
    fileSize: '66 KB',
    steps: [
      'Download the PDF using the button below',
      'Print and fill it out completely',
      'Bring the completed form to your first appointment',
    ],
  },
  {
    id: 'hipaa-notice',
    icon: '🔒',
    title: 'Notice of Privacy Practices',
    badge: 'HIPAA',
    badgeColor: '#1a4a6e',
    description:
      'This notice describes how health information about you may be used and disclosed, and how you can get access to this information. Please review before your visit.',
    fileName: 'HIPAA-Privacy-Practices.pdf',
    filePath: '/forms/dental-hipaa-privacy-practices.pdf',
    fileSize: '229 KB',
    steps: [
      'Download and read this notice carefully',
      'Retain a copy for your personal records',
      'Complete the HIPAA Acknowledgement form below',
    ],
  },
  {
    id: 'hipaa-ack',
    icon: '✅',
    title: 'HIPAA Acknowledgement Form',
    badge: 'Required',
    badgeColor: '#0e8585',
    description:
      'Please download, print, and complete this form before your first dental appointment. This form confirms that you have read our Notice of Privacy Practices.',
    fileName: 'HIPAA-Acknowledgement.pdf',
    filePath: '/forms/dental-hipaa-acknowledgement.pdf',
    fileSize: '295 KB',
    steps: [
      'Download the form below',
      'Sign and date after reading the Privacy Notice',
      'Bring the signed form to your first appointment',
    ],
  },
];

const CHECKLIST = [
  { icon: '🦷', text: 'Completed New Patient Registration form' },
  { icon: '🔒', text: 'Read & signed the HIPAA Acknowledgement form' },
  { icon: '💳', text: 'Insurance card (front & back)' },
  { icon: '🪪', text: 'Valid photo ID (driver\'s license or passport)' },
  { icon: '💊', text: 'List of current medications & dosages' },
  { icon: '📞', text: 'Emergency contact information' },
  { icon: '🏥', text: 'Previous dental records (if available)' },
  { icon: '⏰', text: 'Arrive 10–15 minutes early for your first visit' },
];

function FormCard({ form, index }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={s.formCard}>
      {/* Card header */}
      <div className={s.formCardHeader}>
        <div className={s.formCardLeft}>
          <div className={s.formIconWrap}>
            <span className={s.formIconEmoji}>{form.icon}</span>
            <span className={s.formIndex}>{String(index + 1).padStart(2, '0')}</span>
          </div>
          <div>
            <div className={s.formMeta}>
              <span className={s.formBadge} style={{ background: form.badgeColor }}>
                {form.badge}
              </span>
              <span className={s.formSize}>PDF · {form.fileSize}</span>
            </div>
            <h3 className={s.formTitle}>{form.title}</h3>
          </div>
        </div>
        <button
          className={s.expandBtn}
          onClick={() => setExpanded(e => !e)}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse details' : 'Expand details'}
        >
          <svg
            viewBox="0 0 20 20"
            width="16" height="16"
            fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round"
            style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
          >
            <path d="M5 8l5 5 5-5"/>
          </svg>
        </button>
      </div>

      {/* Description — always visible */}
      <p className={s.formDesc}>{form.description}</p>

      {/* Expanded steps */}
      {expanded && (
        <div className={s.formSteps}>
          <p className={s.formStepsLabel}>Steps:</p>
          <ol className={s.stepsList}>
            {form.steps.map((step, i) => (
              <li key={i} className={s.stepItem}>{step}</li>
            ))}
          </ol>
        </div>
      )}

      {/* Download CTA */}
      <div className={s.formActions}>
        <a
          href={form.filePath}
          download={form.fileName}
          target="_blank"
          rel="noopener noreferrer"
          className={s.downloadBtn}
        >
          <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 3v10M6 9l4 4 4-4"/>
            <path d="M3 17h14"/>
          </svg>
          Download PDF
        </a>
        <button
          className={s.detailToggle}
          onClick={() => setExpanded(e => !e)}
        >
          {expanded ? 'Hide steps' : 'View steps'}
        </button>
      </div>
    </div>
  );
}

export default function NewPatientForms() {
  return (
    <div className={s.root}>

      {/* Intro banner */}
      <div className={s.introBanner}>
        <div className={s.introBannerIcon}>📄</div>
        <div className={s.introBannerText}>
          <h2 className={s.introBannerTitle}>Complete Your Forms Before Your Visit</h2>
          <p className={s.introBannerSub}>
            Save time at your first appointment — download, print, and bring your completed
            forms with you. If you prefer to complete them digitally, contact our office and
            we'll send you a secure link.
          </p>
        </div>
        <a
          href={`tel:${PRACTICE_INFO.phoneRaw}`}
          className={s.introBannerPhone}
        >
          📞 {PRACTICE_INFO.phone}
        </a>
      </div>

      {/* Adobe reader notice */}
      <div className={s.adobeNote}>
        <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="10" cy="10" r="8"/>
          <path d="M10 9v4M10 7h.01"/>
        </svg>
        PDF files require Adobe Reader or any PDF viewer.&nbsp;
        <a href="https://get.adobe.com/reader/" target="_blank" rel="noopener noreferrer" className={s.adobeLink}>
          Download Adobe Reader free →
        </a>
      </div>

      {/* Form cards */}
      <div className={s.formsList}>
        {FORMS.map((form, i) => (
          <FormCard key={form.id} form={form} index={i} />
        ))}
      </div>

      {/* First visit checklist */}
      <div className={s.checklist}>
        <div className={s.checklistHeader}>
          <div className={s.checklistHeaderLeft}>
            <span className={s.checklistIcon}>✅</span>
            <div>
              <h3 className={s.checklistTitle}>What to Bring on Your First Visit</h3>
              <p className={s.checklistSub}>Check everything off before you come in — it helps us get you seen quickly.</p>
            </div>
          </div>
        </div>
        <div className={s.checklistGrid}>
          {CHECKLIST.map((item, i) => (
            <div key={i} className={s.checklistItem}>
              <span className={s.checklistItemIcon}>{item.icon}</span>
              <span className={s.checklistItemText}>{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Help CTA */}
      <div className={s.helpBand}>
        <div className={s.helpBandInner}>
          <div>
            <h3 className={s.helpTitle}>Need Help With Your Forms?</h3>
            <p className={s.helpSub}>
              Our front desk team is happy to assist. Call us or stop by the office — we'll
              make sure your paperwork is ready before you sit in the chair.
            </p>
          </div>
          <div className={s.helpActions}>
            <a href={`tel:${PRACTICE_INFO.phoneRaw}`} className="btn btn-white btn-primary-lg">
              📞 {PRACTICE_INFO.phone}
            </a>
            <Link to="/book-appointment" className={s.helpBook}>
              Book Appointment →
            </Link>
          </div>
        </div>
      </div>

    </div>
  );
}
